import * as React from 'react';
import * as ReactDOM from 'react-dom';

import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { type WrappedFieldProps } from 'redux-form';

import Label, { type LabelProps } from '~/components/Label/Label';
import SelectButton from '~/components/SelectButton';
import SelectLink from '~/components/SelectLink';
import { InputSize, type HeightSize } from '~/constants/form';
import { Keys } from '~/constants/keys';
import { colors, fontWeight } from '~/styles/constants';

import Dropdown from '../Dropdown';

interface SelectListProps {
  width: number;
}

const SelectList = styled('ul')<SelectListProps>`
  padding: 0;
  margin: 0;
  max-height: 300px;
  overflow-y: auto;
`;

interface OptionProps {
  disabled?: boolean;
  isActive: boolean;
  isHighlighted: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClick: (evt: any) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onMouseEnter: (evt: any) => void;
}

const disabledOptionStyle = css`
  cursor: default;
  color: ${colors.gray300};
  font-weight: ${fontWeight.normal};
`;

const Option = styled('li')<OptionProps>`
  padding: 8px 0 8px 30px;
  font-size: 14px;
  line-height: 14px;
  color: ${colors.fontColor};
  ${(props) => (props.isActive ? `font-weight: ${fontWeight.medium};` : '')};
  ${(props) => (props.isHighlighted ? `background-color: ${colors.gray25};` : '')};
  position: relative;
  cursor: pointer;
  ${({ disabled }) => (disabled ? disabledOptionStyle : '')};

  :first-of-type {
    margin-top: 8px;
  }

  :last-of-type {
    margin-bottom: 8px;
  }
`;

const SpinnerContainer = styled('div')`
  width: 100%;
  text-align: center;
  overflow: hidden;
`;

const OptionSelectedIcon = styled('span')`
  position: absolute;
  left: 12px;
  font-size: 10px;
  line-height: 15px;
`;

const OptionDeleteIcon = styled('span')`
  position: absolute;
  right: 12px;
  font-size: 10px;
  line-height: 15px;
`;

const ErrorMessage = styled('div')`
  font-size: 12px;
  color: ${colors.dangerRed};
  margin-top: 5px;
`;

export type Option<T extends boolean | number | string = boolean | number | string> = {
  addToolTip?: boolean;
  description?: string;
  disabled?: boolean;
  icon?: string;
  isDeletable?: boolean;
  label: JSX.Element | string;
  value: T;
};

interface ContainerProps {
  className: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onKeyDown: (evt: any) => void;
  responsive: boolean;
}

const Container = styled('div')<ContainerProps>`
  display: ${({ responsive }) => (responsive ? 'block' : 'inline-block')};
`;

export interface SelectProps {
  className?: string;
  customLabel?: JSX.Element;
  dataAnalytics?: string;

  dataE2E?: string;

  dataPendo?: string;
  dataTestid?: string;
  disabled?: boolean;
  // Props to display a loading state
  displaySpinner?: boolean;
  errorMessage?: string;
  height?: HeightSize;
  hideErrors?: boolean;
  invalid?: boolean;
  isOpen?: boolean;
  label?: string;
  labelProps?: LabelProps;
  onChange?(option: Option): void;
  onDelete?: (
    templateId: number,
    templateName: string,
    evt: React.MouseEvent<HTMLSpanElement, MouseEvent>,
  ) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onKeyDown?: any;
  optionDataAnalytics?: string;
  optionDataPendo?: string;
  options?: Option[];
  placeholder?: string;
  postOptionsContent?: JSX.Element;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  renderSelectButtonContent?: (selectedOption: Option) => any;
  responsive?: boolean;
  size?: InputSize;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  spinnerComponent?: any;
  tabIndex?: number;
  useLinkButton?: boolean;
  value?: Maybe<Option>;
}

interface SelectState {
  highlightedOptionIndex: number;
  isOpen: boolean;
}

class Select<T extends SelectProps> extends React.Component<T, SelectState> {
  static defaultProps: Partial<SelectProps> = {
    size: InputSize.medium,
    useLinkButton: false,
    value: null,
  };

  createOnDeleteCallback =
    (value: number, label: string) => (evt: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
      if (this.props.onDelete) this.props.onDelete(value, label, evt);
    };

  onClick = (evt: React.KeyboardEvent | React.MouseEvent) => {
    evt.stopPropagation();

    this.setState({
      isOpen: !this.state.isOpen,
    });
  };

  onClickOutside = (evt: React.MouseEvent) => {
    // If the element clicked outside is inside trigger, let the trigger on click toggle
    // NOTE: TypeScript tells me that MouseEvent does not have a propagationPath
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((evt as any).propagationPath().indexOf(ReactDOM.findDOMNode(this.trigger)) !== -1) return;

    this.setState({
      isOpen: false,
    });
  };

  onClose = () => {
    this.setState({
      highlightedOptionIndex: 0,
      isOpen: false,
    });
  };

  onKeyDown = (evt: React.KeyboardEvent) => {
    const { options } = this.props;
    const { highlightedOptionIndex } = this.state;
    switch (evt.key) {
      case Keys.ArrowDown:
        if (!this.state.isOpen) return this.onClick(evt);
        evt.preventDefault();
        evt.stopPropagation();

        if (options) {
          const optionsLength = options.length;

          return this.setState((state) => ({
            ...state,
            highlightedOptionIndex:
              state.highlightedOptionIndex + 1 >= optionsLength - 1
                ? optionsLength - 1
                : state.highlightedOptionIndex + 1,
          }));
        }
        break;
      case Keys.ArrowUp:
        evt.preventDefault();
        evt.stopPropagation();
        return this.setState((state) => ({
          ...state,
          highlightedOptionIndex:
            state.highlightedOptionIndex - 1 < 0 ? 0 : state.highlightedOptionIndex - 1,
        }));
      case Keys.Enter:
        // Select hightlighted option
        const selectedOption = (options && options[highlightedOptionIndex]) || { disabled: false };
        const { disabled } = selectedOption;
        if (!this.state.isOpen && !disabled) return this.onClick(evt);
        evt.preventDefault();
        evt.stopPropagation();

        if (options && !disabled) return this.selectOption(options[highlightedOptionIndex]);

        break;
      case Keys.Escape:
      case Keys.Tab:
        // Close and reset
        return this.onClose();
    }
  };

  renderDropdownOption = (option: Option, index: number) => {
    const { disabled } = option;
    const selectedOption = this.props.value;
    const { dataPendo, optionDataAnalytics, optionDataPendo } = this.props;
    let selectedIcon: Maybe<JSX.Element> = null;
    let deleteIcon: Maybe<JSX.Element> = null;
    let optionIsActive = false;
    if (!disabled && selectedOption && option.value === selectedOption.value) {
      selectedIcon = (
        <OptionSelectedIcon>
          <i className="fa fa-check" />
        </OptionSelectedIcon>
      );
      optionIsActive = true;
    }

    deleteIcon = (
      <OptionDeleteIcon
        onClick={this.createOnDeleteCallback(option.value as number, option.label as string)}
      >
        <i className="fa fa-trash" />
      </OptionDeleteIcon>
    );
    return (
      <Option
        data-analytics={optionDataAnalytics}
        data-pendo={optionDataPendo ? optionDataPendo : dataPendo}
        disabled={disabled}
        isActive={optionIsActive}
        isHighlighted={this.state.highlightedOptionIndex === index}
        key={String(option.value)}
        onClick={this.selectOption.bind(this, option)}
        onMouseEnter={this.setHighlightedIndex(index, disabled)}
      >
        {selectedIcon}
        {option.label}
        {option.isDeletable ? deleteIcon : null}
      </Option>
    );
  };

  renderSelectList = () => {
    if (!this.props.options) return null;

    const Spinner = this.props.spinnerComponent || null;

    const spinner = this.props.displaySpinner ? (
      <SpinnerContainer>{Spinner && <Spinner />}</SpinnerContainer>
    ) : null;

    return (
      <SelectList width={this.props.size!}>
        {this.props.options.map(this.renderDropdownOption)}
        {spinner}
      </SelectList>
    );
  };

  selectOption = (option: Option) => {
    // 2nd parameter was event: React.MouseEventHandler<HTMLInputElement>
    // event.preventDefault();
    // event.stopPropagation();

    const { onChange } = this.props;
    const { disabled } = option;

    if (!disabled && onChange) onChange(option);

    if (!disabled) this.onClose();
  };

  setHighlightedIndex = (index: number, disabled?: boolean) => () => {
    if (!disabled) this.setState({ highlightedOptionIndex: index });
  };

  setTriggerRef = (node: React.ReactNode) => (this.trigger = node);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  trigger: any = null;

  constructor(props: T) {
    super(props);

    this.state = {
      highlightedOptionIndex: 0,
      isOpen: false,
    };
  }

  render() {
    const {
      className,
      customLabel,
      dataAnalytics,
      dataE2E,
      dataTestid,
      disabled,
      height,
      invalid,
      label,
      placeholder,
      postOptionsContent,
      renderSelectButtonContent,
      responsive,
      size,
      tabIndex,
      useLinkButton,
    } = this.props;
    const selectedOption = this.props.value;
    let buttonText = selectedOption ? selectedOption.label : placeholder;
    buttonText = renderSelectButtonContent
      ? renderSelectButtonContent(selectedOption!)
      : buttonText;

    const ButtonComponent = useLinkButton ? SelectLink : SelectButton;

    const triggerComponent = () => (
      <ButtonComponent
        buttonRef={this.setTriggerRef}
        className={className}
        dataAnalytics={dataAnalytics}
        dataPendo={this.props.dataPendo}
        dataTestid={dataTestid}
        disabled={disabled}
        hasOptionSelected={!!selectedOption}
        height={height}
        invalid={invalid}
        onClick={this.onClick}
        responsive={responsive}
        size={size}
        tabIndex={tabIndex}
      >
        {buttonText}
      </ButtonComponent>
    );

    let labelComponent = label ? <Label {...this.props.labelProps}>{label}</Label> : null;
    if (customLabel) labelComponent = customLabel;

    return (
      <Container
        className={className as string}
        data-e2e={dataE2E}
        onKeyDown={this.onKeyDown}
        responsive={responsive!}
      >
        {labelComponent}
        <Dropdown
          className={className}
          isOpen={this.state.isOpen || this.props.isOpen}
          minWidth={size}
          onClickOutside={this.onClickOutside}
          onClose={this.onClose}
          triggerComponent={triggerComponent}
        >
          {this.renderSelectList()}
          {postOptionsContent}
        </Dropdown>
        {this.renderErrorMessage()}
      </Container>
    );
  }

  renderErrorMessage() {
    const { errorMessage } = this.props;

    if (!errorMessage) return null;

    return <ErrorMessage>{errorMessage}</ErrorMessage>;
  }
}

export const SelectField = (props: SelectProps & WrappedFieldProps) => {
  const { hideErrors, input, meta, ...selectProps } = props;

  const errorMessage = !hideErrors && meta && meta.touched && meta.error ? meta.error : null;

  return (
    <Select
      onChange={input.onChange}
      value={input.value}
      {...selectProps}
      data-e2e={selectProps.dataE2E}
      errorMessage={errorMessage}
      invalid={!!errorMessage}
    />
  );
};

export default Select;
